import React, { useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CloseOutlined as CloseIcon, ArrowUpwardOutlined as SendIcon } from '@mui/icons-material';
import { colors } from '../../designTokens';
import {
  ASSISTANT_COMPOSER_SURFACE,
  ASSISTANT_CONVERSATION_COLUMN_SX,
  ASSISTANT_THREAD_BG,
} from '../shared/prompt-display/chatPanelSurfaces';
import { ASSISTANT_INTENT_CHIPS, REFINE_HELP_OPTIONS, resolveRefineInstruction } from './assistantIntentChips';

const INTENT_CHIPS = ASSISTANT_INTENT_CHIPS;
const ASSISTANT_LANDING_DRAFT_KEY = 'promptcrew.assistantLandingDraft';

function readAssistantLandingDraft() {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(ASSISTANT_LANDING_DRAFT_KEY) || '';
  } catch {
    return '';
  }
}

function writeAssistantLandingDraft(value) {
  if (typeof window === 'undefined') return;
  try {
    const text = String(value || '');
    if (text) window.sessionStorage.setItem(ASSISTANT_LANDING_DRAFT_KEY, text);
    else window.sessionStorage.removeItem(ASSISTANT_LANDING_DRAFT_KEY);
  } catch {
    // ignore storage failures
  }
}

function IntentSuggestionPanel({ config, customPrompt, onCustomPromptChange, onSelect, onClose, sourceRequired = false, hasSource = false, findMode, onFindModeChange }) {
  const Icon = config.icon;
  return (
    <Box
      sx={{
        width: { xs: 'calc(100vw - 48px)', sm: 380 },
        bgcolor: '#FFFFFF',
        borderRadius: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.25,
          py: 1.35,
          borderBottom: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 17, color: 'text.secondary', opacity: 0.9 }} />
          <Typography variant="body2" fontWeight={650} color="text.primary">
            {config.label}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: 'text.secondary' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {onFindModeChange && (
        <Tabs value={findMode} onChange={(_, value) => onFindModeChange(value)} variant="fullWidth" aria-label="Find type"
          sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: '0.8125rem', fontWeight: 500 } }}>
          <Tab value="find" label="Capabilities" />
          <Tab value="search" label="Knowledge" />
        </Tabs>
      )}
      <Box sx={{ maxHeight: 'min(60dvh, 420px)', overflowY: 'auto', overscrollBehavior: 'contain' }}>
      {config.action === 'refine' && (
        <Typography sx={{ px: 2.25, py: 1.25, fontSize: '0.8125rem', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' }}>
          {hasSource ? 'Source: your composer draft' : 'Paste your capability, then choose a refinement.'}
        </Typography>
      )}
      {config.action !== 'refine' && config.suggestions.map((suggestion) => (
        <Box key={suggestion}>
          <Box
            component="button"
            type="button"
            onClick={() => onSelect(suggestion)}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              px: 2.25,
              py: 1,
              fontSize: '0.875rem',
              lineHeight: 1.45,
              color: 'text.primary',
              fontFamily: 'inherit',
              '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.035)' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
            }}
          >
            {suggestion}
          </Box>
        </Box>
      ))}
      <Divider />
      <Box sx={{ px: 2.25, py: 1.6 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={config.customPlaceholder}
          label={config.action === 'refine' ? (hasSource ? 'Refinement request' : 'Capability text') : undefined}
          error={sourceRequired}
          helperText={sourceRequired ? 'Add capability text before choosing a refinement.' : undefined}
          multiline={config.action === 'refine'}
          minRows={config.action === 'refine' ? 2 : undefined}
          maxRows={config.action === 'refine' ? 6 : undefined}
          value={customPrompt}
          onChange={(e) => onCustomPromptChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && config.action !== 'refine') {
              e.preventDefault();
              onSelect(customPrompt);
            }
          }}
          variant="outlined"
          inputProps={{ 'aria-label': config.customPlaceholder }}
          InputProps={{ endAdornment: (
            <IconButton
              aria-label={`Submit ${config.label.toLowerCase()} request`}
              onClick={() => onSelect(customPrompt)}
              disabled={!customPrompt.trim()}
              size="small"
              sx={{ color: 'primary.main', flexShrink: 0 }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '8px', pr: 0.5 },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
              py: 1.25,
              color: 'text.primary',
              '&::placeholder': { color: colors.textSecondary, opacity: 1 },
            },
          }}
        />
      </Box>
      {config.action === 'refine' && <Box sx={{ px: 1, pb: 1 }}>
        {config.suggestions.map((suggestion) => <Button key={suggestion} onClick={() => onSelect(suggestion)}
          sx={{ width: '100%', justifyContent: 'flex-start', px: 1.25, py: 0.85, color: 'text.primary', fontSize: '0.8125rem', fontWeight: 500, borderRadius: '6px' }}>
          {suggestion}
        </Button>)}
      </Box>}
      </Box>
    </Box>
  );
}

export default forwardRef(function AssistantLanding({
  onSend,
  onBuild,
  onRefine,
  disabled = false,
  refining = false,
  refineOptions = REFINE_HELP_OPTIONS,
  executeMode = 'chat',
  onAddKnowledge,
}, ref) {
  const [message, setMessage] = useState(() => readAssistantLandingDraft());
  useEffect(() => {
    const receiveExample = (event) => {
      if (typeof event.detail === 'string') {
        setMessage(event.detail);
        writeAssistantLandingDraft(event.detail);
      }
    };
    window.addEventListener('promptcrew:onboarding-example', receiveExample);
    return () => window.removeEventListener('promptcrew:onboarding-example', receiveExample);
  }, []);
  const [activeIntent, setActiveIntent] = useState(null);
  const [findMode, setFindMode] = useState('find');
  const [intentMenuAnchor, setIntentMenuAnchor] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [refineSourceRequired, setRefineSourceRequired] = useState(false);

  const intentChips = useMemo(
    () => INTENT_CHIPS.map((chip) => (
      chip.id === 'refine'
        ? {
          ...chip,
          suggestions: refineOptions.map((opt) => opt.label),
          refineOptions,
        }
        : chip
    )),
    [refineOptions]
  );
  const activeConfig = intentChips.find((chip) => chip.id === (activeIntent === 'find' ? findMode : activeIntent));

  useImperativeHandle(ref, () => ({
    setComposerText: (text) => {
      const next = String(text || '');
      setMessage(next);
      writeAssistantLandingDraft(next);
    },
    getComposerText: () => message,
  }), [message]);

  const updateMessage = (value) => {
    setMessage(value);
    writeAssistantLandingDraft(value);
  };

  const toggleIntent = (intentId, anchorEl) => {
    setActiveIntent((prev) => (prev === intentId ? null : intentId));
    setIntentMenuAnchor((prevAnchor) => (activeIntent === intentId && prevAnchor ? null : anchorEl));
    setCustomPrompt('');
  };

  const closeIntentPanel = () => {
    setRefineSourceRequired(false);
    setActiveIntent(null);
    setIntentMenuAnchor(null);
    setCustomPrompt('');
  };

  const submitPrompt = (text, config) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || disabled || refining) return;
    const composerDraft = String(message || '').trim();
    if (config?.action === 'refine') {
      const isShortcut = (config?.refineOptions || REFINE_HELP_OPTIONS).some((opt) => opt.label === trimmed);
      const { instruction, userLabel } = resolveRefineInstruction(trimmed, config);
      const rawText = composerDraft || (isShortcut ? customPrompt.trim() : trimmed);
      if (!rawText) {
        setRefineSourceRequired(true);
        return;
      }
      closeIntentPanel();
      onRefine?.({
        instruction,
        userLabel,
        rawText,
        refineInPlace: Boolean(composerDraft),
      });
      return;
    }
    closeIntentPanel();
    updateMessage('');
    if (config?.action === 'build') {
      onBuild?.(trimmed);
      return;
    }
    onSend?.(trimmed, {
      skipPreflight: Boolean(config?.skipPreflight),
      preflightMode: config?.preflightMode,
    });
  };

  const handleComposerSend = () => {
    submitPrompt(message, { skipPreflight: false, preflightMode: 'auto' });
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        pt: 2,
        // Keep the welcome block slightly above the center of the workspace.
        pb: { xs: '10vh', sm: '14vh' },
        bgcolor: ASSISTANT_THREAD_BG,
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          ...ASSISTANT_CONVERSATION_COLUMN_SX,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          animation: 'assistantWelcomeIn 220ms ease-out both',
          '@keyframes assistantWelcomeIn': {
            from: { opacity: 0, transform: 'translateY(6px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: '100%', mb: 1 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'inherit',
              fontSize: { xs: '1.625rem', sm: '2rem' },
              fontWeight: 400,
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              textWrap: 'balance',
            }}
          >
            {executeMode === 'creator'
              ? <>What would you like to <Box component="span" sx={{ color: 'primary.main' }}>create</Box>?</>
              : <>What are you <Box component="span" sx={{ color: 'primary.main' }}>working</Box> on?</>}
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            position: 'relative',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: ASSISTANT_COMPOSER_SURFACE.borderColor,
            bgcolor: ASSISTANT_COMPOSER_SURFACE.bgcolor,
            px: 2,
            pt: 1.5,
            pb: 1,
            boxShadow: ASSISTANT_COMPOSER_SURFACE.boxShadow,
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            '&:focus-within': {
              borderColor: ASSISTANT_COMPOSER_SURFACE.focusBorderColor,
              bgcolor: ASSISTANT_COMPOSER_SURFACE.bgcolor,
              boxShadow: ASSISTANT_COMPOSER_SURFACE.focusBoxShadow,
            },
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            placeholder="What do you want to accomplish?"
            inputProps={{ 'aria-label': 'What do you want to accomplish?' }}
            variant="standard"
            value={message}
            disabled={disabled || refining}
            onChange={(e) => updateMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleComposerSend();
              }
            }}
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-root': { fontSize: '1rem' },
              '& textarea': {
                resize: 'none',
                pb: 5,
                pt: 0.5,
                fontSize: '1rem',
                lineHeight: 1.5,
                color: 'text.primary',
              },
              '& textarea::placeholder': { color: '#64748B', opacity: 1 },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button size="small" onClick={onAddKnowledge} sx={{ color: 'text.secondary', fontWeight: 500 }}>Add knowledge</Button>
            <IconButton aria-label="Send message" title="Send message" onClick={handleComposerSend} disabled={!message.trim() || disabled || refining} sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, width: '100%', maxWidth: 360 }}>
          {intentChips.filter((chip) => ['find', 'build', 'refine'].includes(chip.id)).map((chip) => {
            const Icon = chip.icon;
            return (
              <Button
                key={chip.id}
                startIcon={<Icon sx={{ fontSize: '16px !important' }} />}
                aria-label={chip.id === 'find' ? 'Find a capability' : chip.id === 'build' ? 'Build a capability' : 'Refine'}
                aria-expanded={activeIntent === chip.id}
                variant="outlined"
                onClick={(event) => toggleIntent(chip.id, event.currentTarget.parentElement)}
                sx={{
                  borderRadius: '8px',
                  minWidth: 0,
                  height: 36,
                  px: 0.5,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  bgcolor: activeIntent === chip.id ? 'rgba(29, 78, 216, 0.09)' : 'transparent',
                  color: activeIntent === chip.id ? 'primary.main' : colors.textPrimary,
                  borderColor: activeIntent === chip.id ? 'rgba(29, 78, 216, 0.26)' : 'rgba(15, 23, 42, 0.1)',
                  boxShadow: activeIntent === chip.id ? '0 1px 4px rgba(29, 78, 216, 0.08)' : 'none',
                  '& .MuiChip-icon': {
                    color: activeIntent === chip.id ? 'primary.main' : colors.textSecondary,
                    opacity: activeIntent === chip.id ? 0.95 : 0.82,
                    ml: 1,
                    mr: -0.25,
                  },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                  '&:hover': {
                    bgcolor: activeIntent === chip.id ? 'rgba(29, 78, 216, 0.12)' : 'rgba(15, 23, 42, 0.035)',
                    borderColor: activeIntent === chip.id ? 'rgba(29, 78, 216, 0.32)' : 'rgba(15, 23, 42, 0.16)',
                  },
                }}
              >{chip.id === 'find' ? 'Find' : chip.id === 'build' ? 'Build' : 'Refine'}</Button>
            );
          })}
        </Box>

        <Menu
          anchorEl={intentMenuAnchor}
          open={Boolean(activeConfig && intentMenuAnchor)}
          onClose={closeIntentPanel}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: '8px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
                maxHeight: 'calc(100dvh - 48px)',
                overflowY: 'auto',
              },
            },
          }}
          MenuListProps={{ sx: { p: 0 } }}
        >
          {activeConfig && (
          <IntentSuggestionPanel
            config={activeConfig}
            findMode={findMode}
            onFindModeChange={activeIntent === 'find' ? (value) => { setFindMode(value); setCustomPrompt(''); } : undefined}
            customPrompt={customPrompt}
            onCustomPromptChange={(value) => { setCustomPrompt(value); setRefineSourceRequired(false); }}
            sourceRequired={refineSourceRequired}
            hasSource={Boolean(message.trim())}
            onSelect={(text) => submitPrompt(text, activeConfig)}
            onClose={closeIntentPanel}
          />
          )}
        </Menu>
      </Box>
    </Box>
  );
});
